import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, FacebookAuthProvider } from 'firebase/auth';
import { getMessaging, getToken, onMessage, Messaging, MessagePayload, isSupported } from "firebase/messaging";

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyBm432CPEloQbqoSJnonh-W_z4QI906R04",
  authDomain: "sportevents-2c368.firebaseapp.com",
  projectId: "sportevents-2c368",
  storageBucket: "sportevents-2c368.firebasestorage.app",
  messagingSenderId: "315164088538",
  appId: "1:315164088538:web:bd110f0bc56dacbb84ede3",
  measurementId: "G-EMGS02R7XN"
};

const firebaseApp = initializeApp(firebaseConfig);

// Initialize Firebase Authentication and get a reference to the service
export const auth = getAuth(firebaseApp);

// Initialize providers
export const googleProvider = new GoogleAuthProvider();
export const facebookProvider = new FacebookAuthProvider();

// Configure providers if needed
googleProvider.addScope('email');
googleProvider.addScope('profile');

facebookProvider.addScope('email');
facebookProvider.addScope('public_profile');

// Global variables for messaging (initialized conditionally)
let messaging: Messaging | null = null;
let messagingInitialized = false;

// Check if environment supports Firebase messaging
const isMessagingSupported = async (): Promise<boolean> => {
  try {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return false;
    }

    // Check for required APIs
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasNotifications = 'Notification' in window;
    const hasSecureContext = window.isSecureContext || window.location.hostname === 'localhost';

    if (!hasServiceWorker || !hasNotifications || !hasSecureContext) {
      return false;
    }

    // Check Firebase messaging support
    const firebaseSupported = await isSupported();

    return firebaseSupported;
  } catch (error) {
    console.warn('Error checking messaging support:', error);
    return false;
  }
};

// Safe messaging initialization
const initializeMessagingIfSupported = async (): Promise<Messaging | null> => {
  if (messagingInitialized) {
    return messaging;
  }

  try {
    const supported = await isMessagingSupported();
    if (!supported) {
      //('Firebase messaging not supported in this environment');
      messagingInitialized = true;
      return null;
    }

    messaging = getMessaging(firebaseApp);
    messagingInitialized = true;
    return messaging;
  } catch (error) {
    console.error('Failed to initialize Firebase messaging:', error);
    messagingInitialized = true;
    return null;
  }
};

// Safe service worker registration
const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  try {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service workers not supported');
      return null;
    }

    const registration = await navigator.serviceWorker.register(
      '/firebase-messaging-sw.js',
      { scope: '/firebase-cloud-messaging-push-scope' }
    );

    console.log('SW registered at scope:', registration.scope);
    return registration;
  } catch (error) {
    console.error('Service worker registration failed:', error);
    return null;
  }
};

// Request permission to show notifications and get the FCM token
export const requestFirebaseNotificationPermission = async (): Promise<string | null> => {
  try {
    const messagingInstance = await initializeMessagingIfSupported();
    if (!messagingInstance) {
      console.log('Messaging not available - skipping notification permission request');
      return null;
    }

    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);

    if (permission !== 'granted') {
      console.warn('Notification permission not granted');
      return null;
    }

    const registration = await registerServiceWorker();
    if (!registration) {
      console.warn('Service worker registration failed');
      return null;
    }

    const token = await getToken(messagingInstance, {
      vapidKey: "BPH3tGjYIn7hKgMXFq2v7AAJCfE8dx0BDsJ3DoGIYtPaFODP20wdbfea7WXpVjRfXpXJgs5B2T3m6RyRGfD4IGs",
      serviceWorkerRegistration: registration
    });

    if (!token) {
      console.warn('Failed to retrieve FCM token');
      return null;
    }

    console.log('FCM Token received:', token);
    return token;
  } catch (err) {
    console.error("An error occurred while retrieving FCM token:", err);
    return null;
  }
};

// Set up foreground notification handler (display browser notification if user is on page)
export const setupForegroundNotifications = async (): Promise<void> => {
  try {
    const messagingInstance = await initializeMessagingIfSupported();
    if (!messagingInstance) {
      console.log('Messaging not available - skipping foreground notifications setup');
      return;
    }

    onMessage(messagingInstance, (payload: MessagePayload) => {
      console.log('Received foreground message:', payload);

      if (Notification.permission !== 'granted') return;

      const notificationTitle = payload.notification?.title || 'New Notification';
      const notificationOptions: NotificationOptions = {
        body: payload.notification?.body || 'You have a new notification',
        icon: '/logo192.png' // Ensure this exists in your public folder
      };

      const notification = new Notification(notificationTitle, notificationOptions);

      notification.onclick = () => {
        window.focus();
        notification.close();
        // Optional: Navigate to a route based on payload data
        // const gameId = payload.data?.gameId;
        // if (gameId) window.location.href = `/games/${gameId}`;
      };
    });

    console.log('Foreground notification handler initialized');
  } catch (error) {
    console.error('Error setting up foreground notifications:', error);
  }
};

// Function to manually trigger a browser notification (for testing)
export const testNotification = (): boolean => {
  try {
    if (!('Notification' in window)) {
      console.warn('Notifications not supported');
      return false;
    }

    if (Notification.permission !== 'granted') {
      console.warn('Notification permission not granted');
      return false;
    }

    new Notification('Test Notification', {
      body: 'This is a test notification to check if notifications are working',
      icon: '/logo192.png'
    });

    return true;
  } catch (error) {
    console.error('Error creating test notification:', error);
    return false;
  }
};

// Promise-based onMessage listener (for custom hooks or events)
export const onMessageListener = async (): Promise<MessagePayload> => {
  const messagingInstance = await initializeMessagingIfSupported();
  if (!messagingInstance) {
    throw new Error('Messaging not supported in this environment');
  }

  return new Promise((resolve) => {
    onMessage(messagingInstance, (payload: MessagePayload) => {
      resolve(payload);
    });
  });
};

// Main initialization function - call this from your React app
export const initializeFirebaseMessaging = async (): Promise<string | null> => {
  try {
    console.log('Initializing Firebase messaging...');
    
    // Check if messaging is supported
    const supported = await isMessagingSupported();
    if (!supported) {
      console.log('Firebase messaging not supported - continuing without push notifications');
      return null;
    }

    // Initialize messaging
    const messagingInstance = await initializeMessagingIfSupported();
    if (!messagingInstance) {
      console.log('Failed to initialize messaging');
      return null;
    }

    // Register service worker
    const registration = await registerServiceWorker();
    if (!registration) {
      console.log('Failed to register service worker');
      return null;
    }

    // Wait for service worker to be ready
    await new Promise<void>((resolve) => {
      if (registration.active) {
        resolve();
      } else {
        registration.addEventListener('updatefound', () => {
          const installingWorker = registration.installing;
          if (installingWorker) {
            installingWorker.addEventListener('statechange', () => {
              if (installingWorker.state === 'activated') {
                resolve();
              }
            });
          }
        });
      }
    });

    // Request permission
    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);

    if (permission !== 'granted') {
      console.log('Notification permission not granted - continuing without notifications');
      return null;
    }

    // Get token
    const token = await getToken(messagingInstance, {
      vapidKey: "BPH3tGjYIn7hKgMXFq2v7AAJCfE8dx0BDsJ3DoGIYtPaFODP20wdbfea7WXpVjRfXpXJgs5B2T3m6RyRGfD4IGs",
      serviceWorkerRegistration: registration
    });

    console.log('FCM Token:', token);
    
    // Set up foreground notifications
    await setupForegroundNotifications();
    
    return token;
  } catch (error) {
    console.error('Firebase messaging initialization error:', error);
    console.log('Continuing without Firebase messaging...');
    return null;
  }
};